import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FaPlus } from "react-icons/fa"
import { FiChevronDown, FiChevronRight, FiCopy, FiTrash2 } from "react-icons/fi"

import useT from "utils/hooks/useT"
import { ConfigContainer, Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import PendingApprovalLink from "components/Lims/ChangeControlLink"
import { limsWorklistTemplates, message } from "state_management"
import {
  WORKLIST_TEMPLATE_STATES,
  selectWorklistTemplates,
} from "state_management/lims/limsWorklistTemplates"
import { selectCanAuthorMasterData } from "state_management/permissions"

// Worklist-templates admin register (F9, SPEC-041 / PRD-008). A capability-admin (planner /
// qa-manager) curates the team's reusable sample-prep worklist templates. ANALYSIS-FIRST (like the
// Analysis Documents register): it lists every analysis definition; each row expands to the worklist
// templates linked to that analysis (version history + the reviewer→supervisor e-sign approval
// lifecycle + "New version"). A separate "Generic examples" section lists templates not bound to a
// definition (the seeded HPLC example) — an admin versions one into a definition-specific template.
//
// Reuses the LimsForskrifter SCSS (the analysis-first table layout) so no new stylesheet is needed.
// SPEC-058 B / BE SPEC-059: the register is open to every LIMS user (read + the "awaiting approval"
// pointer); AUTHORING (add / new version) is planner-only (selectCanAuthorMasterData — the server
// 403s anyone else), and APPROVAL is no longer here at all — it lives in LIMS › Change control.
// The whole route is feature-flagged behind showLims in LimsScreen.
export default function WorklistTemplatesRegister() {
  const { t } = useT("common")
  const dispatch = useDispatch()

  const templates = useSelector(selectWorklistTemplates)
  const definitions = useSelector((state) => state.analysisDefinitions)
  const canAuthor = useSelector(selectCanAuthorMasterData)

  const [expandedId, setExpandedId] = useState(null)
  // null = closed; { mode, base?, definitionId? } drives the editor popup.
  const [editing, setEditing] = useState(null)

  useEffect(() => {
    dispatch(limsWorklistTemplates.fetch())
  }, [dispatch])

  const analyses = useMemo(
    () =>
      Object.values(definitions || {})
        .filter((d) => d && d.name)
        .map((d) => ({ id: d.id, name: d.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [definitions]
  )

  // defId -> its template versions (any state); plus the generic (null-definition) examples.
  const templatesByDefinition = useMemo(() => {
    const byDef = {}
    const generic = []
    templates.forEach((tpl) => {
      if (tpl.analysisDefinitionId) {
        if (!byDef[tpl.analysisDefinitionId]) byDef[tpl.analysisDefinitionId] = []
        byDef[tpl.analysisDefinitionId].push(tpl)
      } else {
        generic.push(tpl)
      }
    })
    return { byDef, generic }
  }, [templates])

  return (
    <div className="LimsForskrifter">
      <div className="LimsForskrifter-header">
        <h1 className="LimsForskrifter-title">{t("limsWorklistTemplatesTitle")}</h1>
        {canAuthor && (
          <Button variant="primary" onClick={() => setEditing({ mode: "create" })}>
            <FaPlus /> {t("limsWorklistTemplateAdd")}
          </Button>
        )}
      </div>
      <p className="LimsForskrifter-intro">{t("limsWorklistTemplatesIntro")}</p>

      {analyses.length === 0 ? (
        <p className="LimsForskrifter-empty">{t("limsAnalysisDocNoAnalyses")}</p>
      ) : (
        <Table striped hover responsive className="LimsForskrifter-table">
          <thead>
            <tr>
              <th></th>
              <th>{t("limsAnalysisDocAnalysis")}</th>
              <th>{t("limsWorklistTemplatesColumn")}</th>
              <th>{t("limsForskriftEffectiveVersion")}</th>
            </tr>
          </thead>
          <tbody>
            {analyses.map((analysis) => {
              const linked = templatesByDefinition.byDef[analysis.id] || []
              const expanded = expandedId === analysis.id
              return (
                <AnalysisRow
                  key={analysis.id}
                  analysis={analysis}
                  linked={linked}
                  expanded={expanded}
                  onToggle={() => setExpandedId(expanded ? null : analysis.id)}
                  onNewVersion={canAuthor ? (base) => setEditing({ mode: "version", base }) : null}
                  onAdd={canAuthor ? () => setEditing({ mode: "create", definitionId: analysis.id }) : null}
                  t={t}
                />
              )
            })}
          </tbody>
        </Table>
      )}

      {/* Generic examples — templates not bound to any analysis definition (the seeded HPLC one).
          An admin versions one into a definition by creating a new version that names a definition. */}
      <h2 className="LimsForskrifter-group-title">{t("limsWorklistTemplatesGeneric")}</h2>
      {templatesByDefinition.generic.length === 0 ? (
        <p className="LimsForskrifter-empty">{t("limsWorklistTemplatesNoGeneric")}</p>
      ) : (
        <div className="LimsForskrifter-versions">
          <TemplateVersions
            versions={templatesByDefinition.generic}
            onNewVersion={canAuthor ? (base) => setEditing({ mode: "version", base }) : null}
            t={t}
          />
        </div>
      )}

      {editing && (
        <WorklistTemplateConfig
          mode={editing.mode}
          base={editing.base || null}
          definitionId={editing.definitionId || null}
          analyses={analyses}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

// One analysis master row + (expanded) its worklist template versions and an "Add worklist" action.
function AnalysisRow({ analysis, linked, expanded, onToggle, onNewVersion, onAdd, t }) {
  const effective = linked.find((tpl) => tpl.state === WORKLIST_TEMPLATE_STATES.EFFECTIVE)
  // #4101: templates standing in the approval chain — `draft` awaits the reviewer,
  // `reviewer-approved` awaits the second signature that makes it effective. Nothing on this row
  // said so, so a template waiting for a signature looked exactly like one nobody had started.
  const pendingCount = linked.filter(
    (tpl) =>
      tpl.state === WORKLIST_TEMPLATE_STATES.DRAFT ||
      tpl.state === WORKLIST_TEMPLATE_STATES.REVIEWER_APPROVED
  ).length
  return (
    <>
      <tr className="LimsForskrifter-row" onClick={onToggle}>
        <td className="LimsForskrifter-chevron">{expanded ? <FiChevronDown /> : <FiChevronRight />}</td>
        <td>{analysis.name}</td>
        <td>
          {linked.length > 0 ? linked.map((tpl) => tpl.name).filter((n, i, a) => a.indexOf(n) === i).join(", ") : t("limsWorklistTemplatesNoneLinkedShort")}
        </td>
        <td>
          {/* #4101: laid out as a flex row so the badges are spaced whether they sit side by side
              or wrap onto separate lines — see AnalysisDocumentsRegister. */}
          <div className="LimsForskrifter-status-group">
            {effective ? (
              <span className="LimsForskrifter-status is-effective">
                {effective.name} · {t("limsForskriftVersion")} {effective.versionNumber}
              </span>
            ) : (
              <span className="LimsForskrifter-status is-none">{t("limsForskriftNoEffective")}</span>
            )}
            {/* Shown ALONGSIDE the effective-version status — a template can be live and still
                have a newer version waiting for its signature. */}
            {pendingCount > 0 && (
              <span
                className="LimsForskrifter-status is-pending"
                title={t("limsForskriftPendingApprovalHint")}
              >
                {t("limsForskriftPendingApproval", { n: pendingCount })}
              </span>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="LimsForskrifter-detail-row">
          <td colSpan={4}>
            <div className="LimsForskrifter-versions">
              {linked.length === 0 ? (
                <p className="LimsForskrifter-help">{t("limsWorklistTemplatesNoneLinked")}</p>
              ) : (
                <TemplateVersions versions={linked} onNewVersion={onNewVersion} t={t} />
              )}
              {onAdd && (
                <div className="LimsForskrifter-attach">
                  <Button variant="primary" size="sm" onClick={onAdd}>
                    <FaPlus /> {t("limsWorklistTemplateAddForAnalysis")}
                  </Button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// The version history table for one lineage (or the generic set): number, name, status, signers,
// the reviewer/supervisor approval actions, and "New version" on the effective one.
function TemplateVersions({ versions, onNewVersion, t }) {
  const members = useSelector((state) => state.members)
  const initials = (sig) => (sig && sig.memberId && members[sig.memberId] && members[sig.memberId].initials) || "—"
  const memberInitials = (id) => (id && members[id] && members[id].initials) || "—"
  const sorted = [...versions].sort(
    (a, b) => (a.name || "").localeCompare(b.name || "") || (a.versionNumber || 0) - (b.versionNumber || 0)
  )
  return (
    <Table size="sm" className="LimsForskrifter-versions-table">
      <thead>
        <tr>
          <th>{t("limsWorklistTemplateName")}</th>
          <th>{t("limsForskriftVersion")}</th>
          <th>{t("limsChangeControlCreatedBy")}</th>
          <th>{t("limsVersionBuiltOnHeader")}</th>
          <th>{t("limsWorklistTemplateSteps")}</th>
          <th>{t("limsForskriftStatus")}</th>
          <th>{t("limsForskriftReviewedBy")}</th>
          <th>{t("limsForskriftSupervisorBy")}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((tpl) => {
          const isSuperseded = tpl.state === WORKLIST_TEMPLATE_STATES.SUPERSEDED
          const isEffective = tpl.state === WORKLIST_TEMPLATE_STATES.EFFECTIVE
          // SPEC-070: a new version builds on the row it is made from — the one in force, or a
          // version still awaiting approval (it then waits for that one before it can be approved).
          // Never a superseded one: the server refuses it, as it would be out of date from birth.
          const isPending =
            tpl.state === WORKLIST_TEMPLATE_STATES.DRAFT || tpl.state === WORKLIST_TEMPLATE_STATES.REVIEWER_APPROVED
          return (
            <tr key={tpl.id} className={isSuperseded ? "LimsForskrifter-row--superseded" : undefined}>
              <td>{tpl.name}</td>
              <td>v{tpl.versionNumber}</td>
              {/* SPEC-070: who made the version, and what it was made on top of. */}
              <td>{memberInitials(tpl.createdBy)}</td>
              <td>{tpl.basedOnVersionNumber ? `v${tpl.basedOnVersionNumber}` : "—"}</td>
              <td>{(tpl.steps || []).length}</td>
              <td>
                <span className={"LimsForskrifter-status " + statusClass(tpl.state)}>{t(statusLabelKey(tpl.state))}</span>
              </td>
              <td>{tpl.reviewerSignature ? initials(tpl.reviewerSignature) : "—"}</td>
              <td>{tpl.supervisorSignature ? initials(tpl.supervisorSignature) : "—"}</td>
              <td className="LimsForskrifter-version-actions">
                <PendingApprovalLink state={tpl.state} itemId={tpl.id} />
                {(isEffective || isPending) && onNewVersion && (
                  <FiCopy
                    className="LimsForskrifter-version-icon"
                    title={t(isEffective ? "limsWorklistTemplateNewVersion" : "limsTemplateContinueFromDraft", {
                      n: tpl.versionNumber,
                    })}
                    onClick={() => onNewVersion(tpl)}
                  />
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

// Add / new-version editor: name + (for a new template) the analysis definition it governs + a
// header-fields list + an ordered steps list (instruction + kind + optional note). A new version is
// pre-filled from the base (its name + definition are fixed — the lineage key). Name is required.
function WorklistTemplateConfig({ mode, base, definitionId, analyses, onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const isVersion = mode === "version"
  const [saving, setSaving] = useState(false)
  const [record, setRecord] = useState(() =>
    base
      ? {
          name: base.name || "",
          analysisDefinitionId: base.analysisDefinitionId || null,
          steps: Array.isArray(base.steps) ? base.steps.map((s) => ({ ...s })) : [],
        }
      : {
          name: "",
          analysisDefinitionId: definitionId || null,
          steps: [{ id: stepId(), instruction: "", kind: "check", note: "" }],
        }
  )
  const setField = (field, value) => setRecord((r) => ({ ...r, [field]: value }))

  const setStep = (idx, field, value) =>
    setRecord((r) => ({ ...r, steps: r.steps.map((s, i) => (i === idx ? { ...s, [field]: value } : s)) }))
  const addStep = () => setRecord((r) => ({ ...r, steps: [...r.steps, { id: stepId(), instruction: "", kind: "check", note: "" }] }))
  const removeStep = (idx) => setRecord((r) => ({ ...r, steps: r.steps.filter((_s, i) => i !== idx) }))

  const handleSubmit = () => {
    const name = (record.name || "").trim()
    if (!name) {
      dispatch(message.error(t("limsWorklistTemplateNameRequired")))
      return false
    }
    const payload = {
      name,
      analysisDefinitionId: record.analysisDefinitionId || null,
      steps: (record.steps || [])
        .map((s) => ({
          id: s.id || stepId(),
          instruction: (s.instruction || "").trim(),
          kind: s.kind || "check",
          note: (s.note || "").trim() || undefined,
        }))
        .filter((s) => s.instruction),
    }
    setSaving(true)
    const verb = isVersion
      ? dispatch(limsWorklistTemplates.createVersion(base.id, payload))
      : dispatch(limsWorklistTemplates.create(payload))
    verb
      .then(() => {
        dispatch(message.success(t(isVersion ? "limsWorklistTemplateVersioned" : "limsWorklistTemplateCreated")))
        if (isVersion) dispatch(limsWorklistTemplates.fetch())
        onClose()
      })
      .catch(() => dispatch(message.error(t("limsWorklistTemplateSaveFailed"))))
      .finally(() => setSaving(false))
    return true
  }

  const analysisCollection = analyses.map((a) => ({ id: a.id, name: a.name }))

  return (
    <Popup nonScrollable onCancel={onClose}>
      <ConfigContainer
        title={isVersion ? t("limsWorklistTemplateNewVersion") : t("limsWorklistTemplateAdd")}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
      >
        {isVersion && <TemplateBaseNote base={base} />}
        <Container>
          <Title>
            {t("limsWorklistTemplateName")} <div className="red-text">*</div>
          </Title>
          {/* The name is fixed on a version bump (the lineage key). */}
          <Text value={record.name || ""} onChange={(v) => setField("name", v)} disabled={isVersion} />
        </Container>
        <Container>
          <Title>{t("limsWorklistTemplateAnalysis")}</Title>
          {/* The analysis definition is fixed on a version bump (the lineage key). A blank value =
              a generic example. */}
          <Select
            isMulti={false}
            collection={analysisCollection}
            value={record.analysisDefinitionId || ""}
            onChange={(v) => setField("analysisDefinitionId", v[0] || null)}
            menuPlacement="bottom"
            disabled={isVersion}
          />
        </Container>

        <Title>{t("limsWorklistTemplateSteps")}</Title>
        {(record.steps || []).map((step, idx) => (
          <div key={step.id || idx} className="LimsForskrifter-field" style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", marginBottom: "0.5rem" }}>
            <div style={{ flex: 3 }}>
              <Text
                value={step.instruction || ""}
                onChange={(v) => setStep(idx, "instruction", v)}
                placeholder={t("limsWorklistTemplateStepInstruction")}
              />
            </div>
            <div style={{ flex: 2 }}>
              <Select
                isMulti={false}
                collection={STEP_KINDS.map((k) => ({ id: k, name: t(stepKindLabelKey(k)) }))}
                value={step.kind || "check"}
                onChange={(v) => setStep(idx, "kind", v[0] || "check")}
                menuPlacement="bottom"
              />
            </div>
            <button
              type="button"
              className="LimsForskrifter-file-action"
              title={t("limsWorklistTemplateStepRemove")}
              onClick={() => removeStep(idx)}
            >
              <FiTrash2 />
            </button>
          </div>
        ))}
        <Button variant="outline-primary" size="sm" disabled={saving} onClick={addStep}>
          <FaPlus /> {t("limsWorklistTemplateAddStep")}
        </Button>
      </ConfigContainer>
    </Popup>
  )
}

// The step kinds an admin can pick (mirrors the seed): a plain CHECK item, a VALUE-capture item, and
// the "analysis performed" COMPLETION item (7a).
const STEP_KINDS = ["check", "value", "completion"]
function stepKindLabelKey(kind) {
  if (kind === "value") return "limsWorklistStepKindValue"
  if (kind === "completion") return "limsWorklistStepKindCompletion"
  return "limsWorklistStepKindCheck"
}

// A client-side step id for new steps (the server persists the steps JSONB verbatim; a stable id per
// step lets the per-sample worklist record completion against it). crypto.randomUUID is available in
// the browser; fall back to a timestamp+counter when it is not (non-security id, only needs to be
// unique within one template).
let _stepCounter = 0
function stepId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `step-${crypto.randomUUID()}`
  }
  _stepCounter += 1
  return `step-${Date.now()}-${_stepCounter}`
}

function statusLabelKey(state) {
  if (state === WORKLIST_TEMPLATE_STATES.EFFECTIVE) return "limsForskriftStatusEffective"
  if (state === WORKLIST_TEMPLATE_STATES.REVIEWER_APPROVED) return "limsForskriftStatusReviewerApproved"
  if (state === WORKLIST_TEMPLATE_STATES.SUPERSEDED) return "limsForskriftStatusSuperseded"
  return "limsForskriftStatusDraft"
}

function statusClass(state) {
  if (state === WORKLIST_TEMPLATE_STATES.EFFECTIVE) return "is-effective"
  if (state === WORKLIST_TEMPLATE_STATES.REVIEWER_APPROVED) return "is-reviewer"
  if (state === WORKLIST_TEMPLATE_STATES.SUPERSEDED) return "is-superseded"
  return "is-draft"
}

// SPEC-070: what a new version is built on, said where it is made. Built on a version still awaiting
// approval, it can only be approved once that one is in force.
function TemplateBaseNote({ base }) {
  const { t } = useT("common")
  const pending =
    base.state === WORKLIST_TEMPLATE_STATES.DRAFT || base.state === WORKLIST_TEMPLATE_STATES.REVIEWER_APPROVED
  return (
    <p className="LimsTemplateBaseNote">
      {t(pending ? "limsTemplateBuiltOnDraft" : "limsTemplateBuiltOnInForce", { n: base.versionNumber })}
    </p>
  )
}
